import { CONSTANTS } from "../../data";
import type { GameState } from "../../types";

export const gameDataReducer = (state: GameState, action: any): GameState => {
  switch (action.type) {
    case "LOAD_GAME":
      return { ...state, ...action.state };

    case "START_NEW_DAY": {
      const { dayIndex } = action;
      let newDailyBlend = state.dailyBlend;

      if (state.dailyBlend.lastUpdatedDay < dayIndex) {
        const eligible = [...CONSTANTS.DAILY_BLEND.eligibleDrinks];
        const trio = [];
        for (let i = 0; i < 3; i++) {
          if (eligible.length === 0) break;
          const randomIndex = Math.floor(Math.random() * eligible.length);
          trio.push(eligible.splice(randomIndex, 1)[0]);
        }
        newDailyBlend = {
          ...state.dailyBlend,
          trio,
          progress: [],
          completed: false,
          buffActive: false,
          lastUpdatedDay: dayIndex,
        };
      }

      return {
        ...state,
        dailyBlend: newDailyBlend,
        environmentalState: {
          ...state.environmentalState,
          dayIndex,
        },
      };
    }

    case "UPDATE_SETTINGS":
      return {
        ...state,
        settings: { ...state.settings, ...action.settings },
      };

    case "SET_CUSTOM_SEED":
      return { ...state, customSeed: action.seed };

    case "APPLY_SEED": {
      const { seedData } = action;
      if (!seedData || !seedData.valid) {
        console.warn("Invalid seed data provided to APPLY_SEED");
        return state;
      }

      try {
        // Create new inventory, preserving tools and adding seed inventory
        const newInventory = { ...CONSTANTS.STARTING_INVENTORY };
        
        // Add seed inventory items
        if (seedData.inventory) {
          Object.entries(seedData.inventory).forEach(([itemId, quantity]) => {
            if (typeof quantity === 'number' && quantity > 0) {
              newInventory[itemId] = quantity;
            }
          });
        }

        // Apply discovered recipes
        const newDiscoveredRecipes = [...seedData.recipes];
        
        // Ensure basic recipes are always discovered
        const basicRecipes = ['beans_grinder', 'ground_coffee_espresso_machine'];
        basicRecipes.forEach(recipe => {
          if (CONSTANTS.RECIPES[recipe] && !newDiscoveredRecipes.includes(recipe)) {
            newDiscoveredRecipes.push(recipe);
          }
        });

        return {
          ...state,
          level: Math.max(1, seedData.level),
          coins: Math.max(CONSTANTS.STARTING_COINS, seedData.coins),
          xp: Math.max(0, seedData.xp),
          prestigeLevel: Math.max(0, seedData.prestigeLevel),
          reputation: Math.max(0, Math.min(100, seedData.reputation || CONSTANTS.REPUTATION_CONFIG.INITIAL_REPUTATION)),
          inventory: newInventory,
          discoveredRecipes: newDiscoveredRecipes,
          seededSession: true,
          screen: "menu", // Go to menu after applying seed
        };
      } catch (error) {
        console.error("Failed to apply seed data:", error);
        // Don't change state if seed application fails
        return state;
      }
    }

    case "RESET_GAME":
      return {
        ...state,
        ...{
          coins: CONSTANTS.STARTING_COINS,
          xp: 0,
          level: 1,
          reputation: CONSTANTS.REPUTATION_CONFIG.INITIAL_REPUTATION,
          inventory: { ...CONSTANTS.STARTING_INVENTORY },
          inventoryActiveTab: 'ingredients',
          inventoryPanelOpen: true,
          customerQueueOpen: true,
          sessionMode: { ...CONSTANTS.DEFAULT_SESSION_MODE },
          seededSession: false,
          customSeed: "",
          brewStations: [
            { slots: [null, null], quantity: 1, gridPosition: { tileX: -1, tileY: -1 } },
          ],
          lastQualityGrade: null,
          customers: [],
          customerIdCounter: 1,
          lastCustomerSpawn: 0,
          discoveredRecipes: [],
          lastDiscovery: null,
          aiGeneratedRecipes: {},
          affogatoEvent: { active: false, endTime: 0, lastTrigger: 0 },
          dailyBlend: {
            trio: [],
            progress: [],
            completed: false,
            buffActive: false,
            buffExpiry: 0,
            lastUpdatedDay: -1,
          },
          criticVisit: {
            active: false,
            endTime: 0,
            lastTrigger: 0,
            currentOrder: 0,
            completedOrders: [],
          },
          nitroUnlocked: false,
          legendaryUnlocks: {
            temperatureControl: false,
            environmentalModifiers: true,
            qualityGrades: false,
          },
          phoenixBlendSequence: {
            active: false,
            progress: [],
            lastServed: 0,
          },
          auroraConditions: {
            active: false,
            icedCount: 0,
            startTime: 0,
          },
          mastersHarmonyConditions: {
            active: false,
            perfectCount: 0,
            goodCount: 0,
            startTime: 0,
          },
          temperatureControl: {
            enabled: false,
            currentTemp: CONSTANTS.TEMPERATURE_CONTROL.defaultTemp,
          },
          environmentalState: {
            enabled: true,
            currentTimeOfDay: "MORNING",
            humidity: 50,
            gameStartTime: Date.now(),
            dayIndex: 0,
            dayProgress: 0,
          },
          qualityGrades: {
            enabled: false,
          },
          activeSeason: null,
          quests: [],
          masterChefChallenge: { 
            active: false,
            id: null,
            startTime: 0,
            progress: 0,
            goal: 0,
            duration: 0
          },
          screen: "intro",
          showModal: null,
          hudHint: null,
          levelUpNotification: null,
          latteArtGame: { active: false },
          trendTip: { message: "", active: false },
          tutorialState: {
            isTutorialActive: false,
            currentTutorial: null,
            completedTutorials: [],
            skippedTutorials: [],
          },
          lastRushScore: {
            score: 0,
            served: 0,
            tips: 0,
            combos: 0,
            legendaries: 0,
            categories: 0,
          },
          rushModeState: {
            originalInventory: null,
            originalDiscoveredRecipes: null,
          },
          aiBarista: {
            active: false,
            level: 0,
            speed: 0,
            efficiency: 0,
          },
          aiTipState: {
            failures: 0,
            timeouts: 0,
            lastFailureTime: 0,
          },
          customerArchetypes: {},
          trendPrediction: {
            lastUpdate: 0,
            trendingItems: [],
          },
          latteArtBuff: {
            active: false,
            endTime: 0,
          },
          isLoading: false,
          equipmentLevels: {
            grinder: 1,
            espresso_machine: 1,
          },
          decorations: {},
          lastCreationTimestamps: {},
          qualityMultipliers: {},
          trendingDrink: { id: null, endTime: 0 },
        },
        settings: state.settings,
        prestigeLevel: state.prestigeLevel,
      };

    default:
      return state;
  }
};
